import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Share, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { initInstallPrompt, installPWA, isAppInstalled } from '../utils/pwa';
import { useLanguage } from '../contexts/LanguageContext';

// Detect iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Detect if in standalone mode (already installed)
const isInStandaloneMode = () => {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

export const PWAInstallPrompt = () => {
  const [installable, setInstallable] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if already installed
    const installed = isAppInstalled() || isInStandaloneMode();
    setIsInstalled(installed);
    
    if (installed) return;
    
    // Check if already dismissed
    const wasDismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
    }

    // For iOS, show custom prompt
    if (isIOS()) {
      setShowIOSPrompt(true);
    } else {
      // For other browsers, initialize install prompt
      initInstallPrompt(setInstallable);
    }
  }, []);

  const handleInstall = async () => {
    const installed = await installPWA();
    if (installed) {
      setInstallable(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowIOSPrompt(false);
    setInstallable(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // Don't show if installed or dismissed
  if (isInstalled || dismissed) {
    return null;
  }

  // Don't show if not installable and not iOS
  if (!installable && !showIOSPrompt) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
      >
        <div className="bg-slate-800/95 backdrop-blur-lg border border-cyan-500/20 rounded-2xl p-4 shadow-xl shadow-cyan-500/10">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-cyan-400" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-sm mb-1">
                {language === 'es' ? 'Instalar BIOSOUNDS' : 'Install BIOSOUNDS'}
              </h3>
              
              {showIOSPrompt ? (
                // iOS Instructions
                <div className="text-slate-400 text-xs mb-3">
                  <p className="mb-2">
                    {language === 'es' 
                      ? 'Para instalar en tu iPhone/iPad:'
                      : 'To install on your iPhone/iPad:'}
                  </p>
                  <ol className="space-y-1.5 ml-1">
                    <li className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-700 text-[10px] text-white">1</span>
                      <span className="flex items-center gap-1">
                        {language === 'es' ? 'Pulsa' : 'Tap'} 
                        <Share className="w-3.5 h-3.5 text-cyan-400 inline mx-0.5" />
                        {language === 'es' ? 'Compartir' : 'Share'}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-700 text-[10px] text-white">2</span>
                      <span className="flex items-center gap-1">
                        {language === 'es' ? 'Selecciona' : 'Select'}
                        <Plus className="w-3.5 h-3.5 text-cyan-400 inline mx-0.5" />
                        {language === 'es' ? '"Añadir a inicio"' : '"Add to Home Screen"'}
                      </span>
                    </li>
                  </ol>
                </div>
              ) : (
                // Standard install prompt
                <>
                  <p className="text-slate-400 text-xs mb-3">
                    {language === 'es' 
                      ? 'Añade la app a tu pantalla de inicio para acceso rápido'
                      : 'Add to your home screen for quick access'}
                  </p>
                  
                  <Button
                    onClick={handleInstall}
                    size="sm"
                    className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-slate-900 font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Instalar App' : 'Install App'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
